/**
 * sRGB hex to OKLCH and back (Björn Ottosson's OKLab), the arithmetic the palette generator in
 * `crates/baton-domain/src/theme/color.rs` uses, kept here once for the client code that needs it:
 * the Appearance page's colour pick (a hex a person chose becomes the seed the route takes), the
 * label shades of `../theme/shades.ts`, and the derived colours of `../emit/layers.ts`' `mix`,
 * resolved as CSS `color-mix(in oklab, …)` computes them.
 */
export interface Oklch {
  readonly l: number;
  readonly c: number;
  /** Degrees, 0 to 360; 0 for a colour with no chroma. */
  readonly h: number;
}

function toLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function toGamma(linear: number): number {
  const c = Math.min(1, Math.max(0, linear));
  return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
}

export function oklchOfHex(hex: string): Oklch {
  const clean = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) throw new Error(`not a hex colour: ${hex}`);
  const r = toLinear(parseInt(clean.slice(0, 2), 16));
  const g = toLinear(parseInt(clean.slice(2, 4), 16));
  const b = toLinear(parseInt(clean.slice(4, 6), 16));
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const chroma = Math.hypot(a, bb);
  return { l: lightness, c: chroma, h: chroma < 0.0005 ? 0 : ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360 };
}

/** The linear sRGB channels of an OKLCH colour, unclipped, so a caller can tell whether the colour is inside the gamut. */
function linearOf({ l, c, h }: Oklch): readonly [number, number, number] {
  const rad = (h * Math.PI) / 180;
  const a = c * Math.cos(rad);
  const b = c * Math.sin(rad);
  const l3 = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m3 = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s3 = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3, -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3, -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3];
}

/** Whether every channel of the colour lies inside sRGB, to a rounding tolerance. */
export function inGamut(colour: Oklch): boolean {
  return linearOf(colour).every((channel) => channel >= -0.0005 && channel <= 1.0005);
}

/** The nearest sRGB hex: a colour outside the gamut is clipped per channel, which moves its hue, so a caller that cares reduces the chroma first (`fitChroma`). */
export function hexOfOklch(colour: Oklch): string {
  return `#${linearOf(colour)
    .map((channel) => Math.round(toGamma(channel) * 255).toString(16).padStart(2, "0"))
    .join("")}`;
}

/** The colour with its chroma reduced, by bisection, to the most the gamut holds at its lightness and hue, so its hex keeps its hue. */
export function fitChroma(colour: Oklch): Oklch {
  if (inGamut(colour)) return colour;
  let low = 0;
  let high = colour.c;
  for (let i = 0; i < 24; i++) {
    const mid = (low + high) / 2;
    if (inGamut({ ...colour, c: mid })) low = mid;
    else high = mid;
  }
  return { ...colour, c: low };
}

/**
 * CSS `color-mix(in oklab, first p, second)` with `p` as `share` (0 to 1): the two colours interpolated
 * on OKLab's lightness and its two opponent axes, which is why it goes through the cartesian form and
 * not the hue angle. A mix of two sRGB colours can land a hair outside the gamut, and the hex clips it.
 */
export function mixOklab(first: string, second: string, share: number): string {
  const [a, b] = [oklchOfHex(first), oklchOfHex(second)];
  const axes = (colour: Oklch) => {
    const rad = (colour.h * Math.PI) / 180;
    return [colour.l, colour.c * Math.cos(rad), colour.c * Math.sin(rad)] as const;
  };
  const [al, aa, ab] = axes(a);
  const [bl, ba, bb] = axes(b);
  const l = al * share + bl * (1 - share);
  const x = aa * share + ba * (1 - share);
  const y = ab * share + bb * (1 - share);
  const c = Math.hypot(x, y);
  return hexOfOklch({ l, c, h: c < 0.0005 ? 0 : ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360 });
}
